import type { Block } from '../functions/_lib/blocks';
import type { EngagementScore } from '../functions/_lib/engagement';
import type { SegmentQuery } from '../functions/_lib/segments';

export type Subscriber = {
  row: number;
  name: string;
  email: string;
  date: string | null;
  source: string;
  status: 'subscribed' | 'unsubscribed';
  unsubAt: string | null;
  tags: string[];
  notes: string;
};

export type SendResult = {
  ok: boolean;
  sent: number;
  failed: number;
  errors: { email: string; error: string }[];
  broadcastId?: string;
};

export type AuditEntry = {
  timestamp: string;
  admin: string;
  action: string;
  subject: string;
  recipients: number;
  sent: number;
  failed: number;
  sources: string;
  testEmail: string;
  errors: string;
  recipientEmails: string;
  bodyHtml: string;
  excludedCount?: number;
};

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  const text = await res.text();
  let data: any;
  try { data = JSON.parse(text); }
  catch { throw new Error(`Non-JSON response (${res.status}): ${text.slice(0, 200)}`); }
  if (!res.ok || data?.ok === false) {
    throw new Error(data?.error || `Request failed (${res.status})`);
  }
  return data as T;
}

export type DryRunResult = {
  ok: true;
  dryRun: true;
  recipients: number;
  sample: { name: string; email: string; source?: string }[];
  sources?: string[] | null;
};

export const api = {
  listSubscribers: () =>
    jsonFetch<{ ok: true; subscribers: Subscriber[] }>('/api/subscribers'),

  listAudit: (limit = 100) =>
    jsonFetch<{ ok: true; entries: AuditEntry[] }>(`/api/audit?limit=${limit}`),

  addSubscriber: (input: {
    name?: string;
    email: string;
    source?: string;
    status?: 'subscribed' | 'unsubscribed';
    tags?: string[];
    notes?: string;
  }) =>
    jsonFetch<{ ok: true; email: string; row: number }>('/api/subscribers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),

  deleteSubscriber: (email: string, row?: number) =>
    jsonFetch<{ ok: true; email: string; row: number }>(
      `/api/subscribers?email=${encodeURIComponent(email)}${row ? `&row=${row}` : ''}`,
      { method: 'DELETE' },
    ),

  setSubscriberStatus: (email: string, status: 'subscribed' | 'unsubscribed') =>
    jsonFetch<{ ok: true; email: string; status: string; row: number }>('/api/subscriber-status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, status }),
    }),

  send: (
    subject: string,
    blocks: Block[],
    opts?: {
      preheader?: string;
      isMarketing?: boolean;
      sources?: string[];
      segmentId?: number;
      emails?: string[];
      tracking?: boolean;
      excludedEmails?: string[];
    },
  ) =>
    jsonFetch<SendResult>('/api/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject, blocks, ...opts }),
    }),

  sendTest: (
    subject: string,
    blocks: Block[],
    testEmail: string,
    opts?: { preheader?: string; tracking?: boolean; isMarketing?: boolean },
  ) =>
    jsonFetch<SendResult>('/api/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject, blocks, testEmail, ...opts }),
    }),

  dryRun: (subject: string, blocks: Block[], sources?: string[]) =>
    jsonFetch<DryRunResult>('/api/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject, blocks, dryRun: true, sources }),
    }),

  engagement: () =>
    jsonFetch<{ ok: true; engagement: Record<string, EngagementSummary> }>('/api/engagement'),

  listFollowUps: () =>
    jsonFetch<{ ok: true; followUps: Record<string, FollowUpEntry> }>('/api/followups'),

  setFollowUp: (email: string, at: number | null, note: string) =>
    jsonFetch<{ ok: true; email: string; at: number | null; note: string }>('/api/followups', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, at, note }),
    }),

  getProfile: (email: string) =>
    jsonFetch<ProfileResponse>(`/api/profile/${encodeURIComponent(email)}`),

  setTags: (email: string, tags: string[]) =>
    jsonFetch<{ ok: true; email: string; tags: string[]; row: number }>('/api/tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, tags }),
    }),

  setNotes: (email: string, notes: string) =>
    jsonFetch<{ ok: true; email: string; row: number }>('/api/notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, notes }),
    }),

  listSegments: () =>
    jsonFetch<{ ok: true; segments: Segment[] }>('/api/segments'),

  saveSegment: (input: { id?: number; name: string; query: SegmentQuery }) =>
    jsonFetch<{ ok: true; id: number }>('/api/segments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),

  deleteSegment: (id: number) =>
    jsonFetch<{ ok: true }>(`/api/segments?id=${id}`, { method: 'DELETE' }),

  listBroadcastStats: () =>
    jsonFetch<{ ok: true; broadcasts: BroadcastStat[] }>('/api/broadcast-stats'),

  getBroadcastDetail: (id: string) =>
    jsonFetch<BroadcastDetail>(`/api/broadcast-stats?b=${encodeURIComponent(id)}`),

  previewSegment: (query: SegmentQuery) =>
    jsonFetch<{ ok: true; count: number; sample: { name: string; email: string; source: string; tags: string[] }[] }>(
      '/api/segments',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preview: true, query }),
      },
    ),
};

export type Segment = {
  id: number;
  name: string;
  query: SegmentQuery;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
};

export type { SegmentQuery };

export type EngagementSummary = {
  opens: number;
  clicks: number;
  lastOpenAt: number | null;
  score: EngagementScore;
};

export type ProfileEvent = {
  id: number;
  broadcast_id: string;
  email: string;
  type: 'open' | 'click' | 'open_bot' | 'bounce' | 'complaint';
  link_id: number | null;
  user_agent: string | null;
  ip_hash: string | null;
  bucket: number;
  created_at: number;
  broadcast_subject: string;
};

export type ProfileResponse = {
  ok: true;
  subscriber: Subscriber;
  events: ProfileEvent[];
  engagement: EngagementSummary;
};

export type FollowUpEntry = {
  at: number | null;
  note: string;
};

export type BroadcastStat = {
  id: string;
  subject: string;
  createdAt: number;
  recipients: number;
  trackingEnabled: boolean;
  uniqueOpens: number;
  uniqueClicks: number;
  totalClicks: number;
};

export type BroadcastDetail = {
  ok: true;
  broadcast: {
    id: string;
    subject: string;
    createdAt: number;
    recipients: number;
    trackingEnabled: boolean;
  };
  links: {
    linkId: number;
    url: string;
    label: string | null;
    clickers: { email: string; clicks: number; firstAt: number }[];
  }[];
  openers: { email: string; firstAt: number }[];
};
